import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Coins, 
  TrendingUp, 
  Users, 
  AlertTriangle, 
  Building2, 
  BarChart3,
  Shield,
  Activity,
  DollarSign,
  Layers
} from 'lucide-react';
import { Card, Badge } from '../../components/ui';
import { api } from '../../lib/api';

interface PlatformStats {
  totalAssets: number;
  totalTokenizedValue: number;
  totalHolders: number;
  tradingVolume24h: number;
  activeIssuers: number;
  pendingApprovals: number;
  complianceAlerts: number;
  avgCoverageRatio: number;
}

interface CategoryStats {
  category: string;
  assetCount: number;
  totalValue: number;
  holderCount: number;
}

interface TopIssuer {
  tenantId: string;
  tenantName: string;
  assetCount: number;
  totalValue: number;
}

export function TokenizationDashboard() {
  const [period, setPeriod] = useState('30d');

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['tokenization-platform-stats', period],
    queryFn: async () => {
      const res = await api.get<PlatformStats>('/tokenization/dashboard/platform-stats', {
        params: { period }
      });
      return res.data;
    }
  });

  const { data: categoryStats } = useQuery({
    queryKey: ['tokenization-category-stats'],
    queryFn: async () => {
      const res = await api.get<CategoryStats[]>('/tokenization/dashboard/category-stats');
      return res.data;
    }
  });

  const { data: topIssuers } = useQuery({
    queryKey: ['tokenization-top-issuers'],
    queryFn: async () => {
      const res = await api.get<TopIssuer[]>('/tokenization/dashboard/top-issuers');
      return res.data;
    }
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      STABLECOIN: 'Stablecoin',
      RECEIVABLE: 'Recebíveis',
      REAL_ESTATE: 'Imóveis',
      FUND_SHARE: 'Cotas de Fundos',
      FIXED_INCOME: 'Renda Fixa',
      COMMODITY: 'Commodities',
      PRECATORY: 'Precatórios'
    };
    return labels[category] || category;
  };

  if (statsLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-surface-800 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Dashboard de Tokenização</h1>
          <p className="text-surface-400">Visão geral da plataforma de tokenização institucional</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
        >
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
          <option value="90d">Últimos 90 dias</option>
          <option value="1y">Último ano</option>
        </select>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-500/20 rounded-xl">
              <Coins className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Total Tokenizado</p>
              <p className="text-2xl font-bold text-surface-100">
                {formatCurrency(stats?.totalTokenizedValue || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500/20 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Volume 24h</p>
              <p className="text-2xl font-bold text-surface-100">
                {formatCurrency(stats?.tradingVolume24h || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Total de Holders</p>
              <p className="text-2xl font-bold text-surface-100">
                {formatNumber(stats?.totalHolders || 0)}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-500/20 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-surface-400 text-sm">Alertas de Compliance</p>
              <p className="text-2xl font-bold text-surface-100">
                {stats?.complianceAlerts || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-surface-400" />
            <div>
              <p className="text-surface-400 text-xs">Ativos Tokenizados</p>
              <p className="text-lg font-semibold text-surface-100">{stats?.totalAssets || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-surface-400" />
            <div>
              <p className="text-surface-400 text-xs">Emissores Ativos</p>
              <p className="text-lg font-semibold text-surface-100">{stats?.activeIssuers || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-surface-400" />
            <div>
              <p className="text-surface-400 text-xs">Aprovações Pendentes</p>
              <p className="text-lg font-semibold text-surface-100">{stats?.pendingApprovals || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-surface-400" />
            <div>
              <p className="text-surface-400 text-xs">Cobertura Média (PoR)</p>
              <p className="text-lg font-semibold text-surface-100">
                {((stats?.avgCoverageRatio || 0) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Stats */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">Por Categoria</h3>
          <div className="space-y-4">
            {categoryStats?.map((cat) => (
              <div key={cat.category} className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
                <div>
                  <p className="font-medium text-surface-100">{getCategoryLabel(cat.category)}</p>
                  <p className="text-sm text-surface-400">{cat.assetCount} ativos • {cat.holderCount} holders</p>
                </div>
                <p className="text-lg font-semibold text-primary-400">
                  {formatCurrency(cat.totalValue)}
                </p>
              </div>
            ))}
            {(!categoryStats || categoryStats.length === 0) && (
              <p className="text-surface-500 text-center py-4">Nenhum dado disponível</p>
            )}
          </div>
        </Card>

        {/* Top Issuers */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-surface-100 mb-4">Top Emissores</h3>
          <div className="space-y-4">
            {topIssuers?.slice(0, 5).map((issuer, index) => (
              <div key={issuer.tenantId} className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-primary-500/20 rounded-full text-primary-400 text-sm font-medium">
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-surface-100">{issuer.tenantName}</p>
                    <p className="text-sm text-surface-400">{issuer.assetCount} ativos</p>
                  </div>
                </div>
                <p className="text-lg font-semibold text-primary-400">
                  {formatCurrency(issuer.totalValue)}
                </p>
              </div>
            ))}
            {(!topIssuers || topIssuers.length === 0) && (
              <p className="text-surface-500 text-center py-4">Nenhum emissor encontrado</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
